import { findLast } from 'lodash';
import { useState } from 'react';

import { useTracking } from '@project44/ui-utils';
import {
  GeofenceResponse,
  TrackedShipmentEvent,
  TrackedShipmentPosition,
  TrackedShipmentRouteSegment,
  TrackedShipmentState,
  TrackedShipmentStop,
} from '@project44-models/shipment-entity';

import { EntityEnum } from '../../definitions/enums/Entity';
import {
  getCurrentRouteSegmentId,
  getShipmentRoute,
} from '../../utils/helpers/routeUtils';
import { scopedT } from '../../utils/helpers/scopedT';
import { isShipmentCompleted } from '../../utils/helpers/shipment';
import {
  adjustStopsCoords,
  mapStopsToMapMarkers,
  shouldDisplayCurrentPosition,
} from '../../utils/helpers/shipmentMap';
import { Segment } from '../../utils/types';
import { Collapsible } from '../Collapsible';
import { getSegmentIcon } from '../RouteIcons/shipmentSegment';
import { DisplayMap } from './components/DisplayMap';
import { MVMT_SHIPMENT_ENTITY_ADJUST_MAP } from './components/DisplayMap/track';
import LocationPingsTable from './components/LocationPingsTable';

import { LocationPingsPanel, StyledPanel } from './styles';

interface ShipmentMapRoutes {
  stops: TrackedShipmentStop[];
  events: TrackedShipmentEvent[];
  states: TrackedShipmentState[];
  routeSegments: TrackedShipmentRouteSegment[];
  positions: TrackedShipmentPosition[];
  geofences: GeofenceResponse[];
}

interface MapPanelProps {
  data: ShipmentMapRoutes[];
  entityType: EntityEnum;
  entityId: string;
  isChina?: boolean;
}

const Observer = function <T>() {
  let thisCb: null | ((p: T) => void) = null;

  const on = (cb: (p: T) => void) => (thisCb = cb);
  const trigger = (data: T) => thisCb?.(data);

  return { on, trigger };
};

const t = scopedT();

export const MapPanel = ({
  data,
  entityType,
  entityId,
  isChina,
}: MapPanelProps) => {
  const trackEvents = useTracking();

  const handleExpandTracking = (adjustmentType: string) => {
    trackEvents.track({
      eventName: MVMT_SHIPMENT_ENTITY_ADJUST_MAP,
      properties: {
        adjustmentType: adjustmentType,
        totalPlottableRouteStops: data.flatMap((i) => i.stops).length,
      },
    });
  };

  const mapClickObserver = Observer<TrackedShipmentPosition>();
  const tableClickObserver = Observer<TrackedShipmentPosition>();

  const [pingsVisible, setPingsVisisble] = useState(false);

  const routesData = data.map(
    ({ stops, events, states, routeSegments, positions, geofences }) => {
      const adjustedStops = adjustStopsCoords(stops);
      const route = getShipmentRoute(adjustedStops, routeSegments);
      const currentSegmentId = getCurrentRouteSegmentId(route, events, states);
      const lastPos = findLast(positions, ({ dateTime }) => !!dateTime);
      const shouldDisplayCurrent =
        !!lastPos &&
        !isShipmentCompleted(states) &&
        shouldDisplayCurrentPosition(states);

      const currentPosition = shouldDisplayCurrent ? lastPos : undefined;

      const markers = mapStopsToMapMarkers(
        stops.filter((s) => route.map((i) => i.id).includes(s.id)),
        shouldDisplayCurrent,
        states,
        events,
        currentSegmentId,
      );

      const currentSegment = (route as Segment[]).find(
        ({ id }) => id === currentSegmentId,
      );

      const routeIcon =
        currentSegment?.type === 'stop'
          ? 'locationmark'
          : getSegmentIcon(currentSegment);

      return {
        stops: adjustedStops,
        icon: routeIcon,
        markers,
        geofences,
        positions,
        currentPosition,
        currentSegmentId,
      };
    },
  );

  const togglePings = () => {
    // it should happen after collaspible panel expansion finihed
    setTimeout(() => {
      const workspace = document.getElementById('workspace-entities-container');
      const shipment = document.getElementById(entityId);
      const pings = document.getElementById(`location-pings-${entityId}`);

      if (workspace && shipment && pings) {
        const PADDING = 40;
        workspace.scrollTo(
          shipment.offsetLeft +
            pings.offsetLeft +
            pings.clientWidth -
            workspace.clientWidth +
            PADDING,
          0,
        );
      }
    }, 500);
    setPingsVisisble((state) => !state);
  };

  const panelStyles = pingsVisible
    ? {
        borderTopRightRadius: '0',
        borderBottomRightRadius: '0',
      }
    : {};

  return (
    <>
      <StyledPanel
        title={t('map.title')}
        entityType={entityType}
        expandable={true}
        onExpand={() => handleExpandTracking('expand')}
        onCollapse={() => handleExpandTracking('collapse')}
        sx={panelStyles}
      >
        <DisplayMap
          routes={routesData}
          togglePings={togglePings}
          watch={tableClickObserver.on}
          onChangeActivePing={mapClickObserver.trigger}
          isChina={isChina}
        />
      </StyledPanel>
      <Collapsible active={pingsVisible}>
        <LocationPingsPanel
          data-testid="locations-pings-panel"
          id={`location-pings-${entityId}`}
          entityType={entityType}
          title={t('map.pings.title')}
          panelItemsCount={data.flatMap((i) => i.positions).length}
          onClose={() => setPingsVisisble(false)}
          closable
        >
          <LocationPingsTable
            data={data.flatMap((i) => i.positions)}
            watch={mapClickObserver.on}
            onChangeActivePing={tableClickObserver.trigger}
          />
        </LocationPingsPanel>
      </Collapsible>
    </>
  );
};

export { MapSkeleton } from './components/MapSkeleton';
